import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Popover } from '@barges/react';
import { salesPriceType } from '../../../../config/const';
import { isXfmProgram, getXfmFreeSetupMin, isPSMobile } from '../../../../config/util';
import { modeIsOnline, getModeStatusColor } from './style_config';
import classes from './classes.module.scss';
import {
  onStartChat, onStartVoip,
  trackProfileChatButtonAction, makeActionIfNeed, renderfreeMinLabel
} from './advisor_cta_utils';
import { isWebView } from '../../../../../common/config/utils';
import { SvgIcon } from '../../../../../common/sprites';
import { advisorCtaThemes } from './advisor_cta_themes';
import { ForexAdvisorCTA } from './forex_advisor_cta';
import { DefaultAdvisorCTA } from './default_cta';

const getImageId = (mode) => {
  if (mode === 'chat') return 'chat_preview_cta';
  return isWebView() ? 'voice_preview_cta_disabled' : 'voice_preview_cta';
};

const renderPreviewAdvisorCTA = (mode, onClick, t) => (
  <button disabled={ isWebView() && mode !== 'chat' } style={ isWebView() && mode !== 'chat' ? { backgroundColor: '#EAEAEA', color: '#A1A1A1' } : {} } type="button" className={ classes.previewAdvisorCTA } onClick={ (e) => onClick({ e }) }>
    <SvgIcon id={ getImageId(mode) } className={ classes.previewCtaIcon } />
    {mode === 'chat' ? t('advisor_profile.chat_now') : t('advisor_profile.call_now')}
  </button>
);

const getActualPrice = ({ promoPrices, userAdvisorModeSalePrice, price }) => {
  if (userAdvisorModeSalePrice === salesPriceType.personalSalesPrice) return price;
  const { salesPrice } = promoPrices;
  if (salesPrice) return salesPrice;
  return price;
};

function AdvisorCTA({
  id,
  mode,
  modeState,
  price,
  localPrice,
  currency,
  displayRegister,
  authPendingAction,
  startChat,
  name,
  advisorCallProps,
  type,
  chatInit,
  makeCallOrShowPopup,
  theme,
  promoPrices,
  localPromoPrices,
  fxRate,
  t,
  liveModes,
  xfmProgram = {},
  userXfmProgram = {},
  needCheckAction,
  userAdvisorModeSalePrice,
  isDefaultBottomCTA = false
}) {
  const [modeUnavailablePoppupVisible, setModeUnavailablePoppupVisible] = useState(null);
  const location = useLocation();
  const history = useNavigate();

  const defaultStartProps = () => ({
    price: getActualPrice({ promoPrices, userAdvisorModeSalePrice, price }), displayRegister, authPendingAction, location,
    mode, startChat, chatInit, fxRate, currency
  });

  const hideModeUnavailablePoppup = () => {
    setModeUnavailablePoppupVisible(false);
  };

  const renderModeUnavailablePoppup = () => (
    <Popover
      isOpen={ modeUnavailablePoppupVisible }
      centered
    >
      <div className={ classes.modeUnavailablePoppupContainer }>
        {t('advisor_profile.advisor_mode_just_becomes_unavailable')}
        <div
          onClick={ hideModeUnavailablePoppup }
          onKeyDown={ hideModeUnavailablePoppup }
          className={ classes.modeUnavailablePoppupBtn }
        >
          { t('general.ok') }
        </div>
      </div>
    </Popover>
  );

  const advisorCTAOnClick = ({ action, e }) => {
    e?.preventDefault();
    if (isWebView() && (mode === 'voice' || mode === 'video')) {
      return;
    }
    if (!modeIsOnline(modeState)) {
      if (action) setModeUnavailablePoppupVisible(true);
      return;
    }
    const requestParams = { context: 'advisor_cta' };
    trackProfileChatButtonAction({
      price, id, mode, location
    });
    if (mode === 'chat') {
      onStartChat({ ...defaultStartProps(), ...advisorCallProps, requestParams });
      return;
    }
    onStartVoip({
      ...defaultStartProps(), makeCallOrShowPopup, ...advisorCallProps, requestParams
    });
  };

  useEffect(() => {
    if (needCheckAction) {
      makeActionIfNeed({
        mode, advisorCTAOnClick, location, history
      });
    }
  }, []);

  const freeMinLabel = useMemo(() => {
    if (theme === advisorCtaThemes.advisorFixContainer) return null;
    if (!isXfmProgram({ advisorXfmProgram: xfmProgram, userXfmProgram })) return null;
    const { color } = getModeStatusColor({
      mode, modeState, liveModes, isFreeMinLabel: true
    });
    const freeMinText = t('advisor_profile.free_min', { amount: getXfmFreeSetupMin() });
    return renderfreeMinLabel(color, freeMinText);
  }, [xfmProgram, userXfmProgram, modeState]);

  if (type === 'advisorPreviewCta') {
    return renderPreviewAdvisorCTA(mode, advisorCTAOnClick, t);
  }

  if (!isPSMobile() || isDefaultBottomCTA) {
    return (
      <DefaultAdvisorCTA
        theme={ theme }
        mode={ mode }
        modeState={ modeState }
        liveModes={ liveModes }
        name={ name }
        t={ t }
        localPromoPrices={ localPromoPrices }
        localPrice={ localPrice }
        userAdvisorModeSalePrice={ userAdvisorModeSalePrice }
        currency={ currency }
        advisorCTAOnClick={ advisorCTAOnClick }
        freeMinLabel={ freeMinLabel }
        modeUnavailablePoppupVisible={ modeUnavailablePoppupVisible }
        renderModeUnavailablePoppup={ renderModeUnavailablePoppup }
      />
    );
  }

  return (
    <ForexAdvisorCTA
      mode={ mode }
      modeState={ modeState }
      liveModes={ liveModes }
      name={ name }
      t={ t }
      localPromoPrices={ localPromoPrices }
      localPrice={ localPrice }
      userAdvisorModeSalePrice={ userAdvisorModeSalePrice }
      currency={ currency }
      advisorCTAOnClick={ advisorCTAOnClick }
      freeMinLabel={ freeMinLabel }
      modeUnavailablePoppupVisible={ modeUnavailablePoppupVisible }
      renderModeUnavailablePoppup={ renderModeUnavailablePoppup }
    />
  );
}

export default AdvisorCTA;
